import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMPUS_PHOTO_MAX_BYTES,
  CAMPUS_PHOTO_TYPES,
  CampusEmailVerifyInputSchema,
  GrowthCodeSchema,
  type CampusPassMethod,
  type CampusSummary,
  type MyCampusPass,
} from '@capsico/contracts';
import { errorMessage } from '../api/client';
import { fetchCampus, startCampusPass, uploadCampusPhoto, verifyCampusEmail } from '../api/growth';
import { pointKey, useDeliveryPoint } from './useDeliveryPoint';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';

export const campusKey = (where: string) => ['campus', where] as const;

/** What is draft-saved: the campus, the method, the college email, an ambassador code. Never the email code. */
interface CampusForm {
  campusId: string | null;
  method: CampusPassMethod;
  email: string;
  ambassadorCode: string;
}

const EMPTY_FORM: CampusForm = { campusId: null, method: 'email', email: '', ambassadorCode: '' };

type PhotoType = (typeof CAMPUS_PHOTO_TYPES)[number];
const isPhotoType = (type: string): type is PhotoType =>
  (CAMPUS_PHOTO_TYPES as readonly string[]).includes(type);

function emailMatches(email: string, campus: CampusSummary | null): boolean {
  if (!campus || campus.emailDomains.length === 0) return true;
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return campus.emailDomains.some(
    (allowed) => domain === allowed || domain.endsWith(`.${allowed}`),
  );
}

/**
 * Campus Pass (W18): find campuses near the customer (GPS or the delivery point), apply with a
 * college email code or a student ID photo (presigned upload → complete), and show the pass
 * status. The form is draft-saved; the 6-digit email code lives only in memory and is never
 * written to the phone.
 */
export function useCampusPass(
  enabled: boolean,
  messages: {
    emailBad: string;
    tooBig: string;
    sent: string;
    verified: string;
    uploaded: string;
    failed: string;
  },
) {
  const client = useQueryClient();
  const point = useDeliveryPoint();
  const [near, setNear] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const where = near ?? (point ? { lat: point.lat, lng: point.lng } : null);
  const key = campusKey(pointKey(where));

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchCampus(where),
    enabled,
    staleTime: 60_000,
  });
  const data: MyCampusPass | null = query.data ?? null;
  const pass = data?.pass ?? null;

  const [form, setForm] = useState<CampusForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  /** The email code: memory only (never in a draft, never in storage). */
  const [code, setCode] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const preview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const draft = useFormDraft<CampusForm>('campus-pass:new', form, {
    enabled: enabled && data !== null,
    isEmpty: (value) => !value.campusId && !value.email.trim() && !value.ambassadorCode.trim(),
    base: EMPTY_FORM,
  });

  const campuses = data?.campuses ?? [];
  const campus = campuses.find((entry) => entry.id === form.campusId) ?? null;

  const store = (next: MyCampusPass) => client.setQueryData(key, next);

  const start = useMutation({
    mutationFn: () =>
      startCampusPass({
        campusId: form.campusId ?? '',
        method: form.method,
        ...(form.method === 'email' ? { email: form.email.trim() } : {}),
        ...(form.ambassadorCode.trim()
          ? { ambassadorCode: GrowthCodeSchema.parse(form.ambassadorCode) }
          : {}),
      }),
    onSuccess: (next) => {
      store(next);
      setError(null);
      setCode('');
      if (form.method === 'email') showToast(messages.sent, { tone: 'positive' });
    },
    onError: (failure) => setError(errorMessage(failure, messages.failed)),
  });

  const verify = useMutation({
    mutationFn: (digits: string) => verifyCampusEmail(digits),
    onSuccess: (next) => {
      store(next);
      setCode('');
      setError(null);
      draft.clear();
      setForm(EMPTY_FORM);
      showToast(messages.verified, { tone: 'positive' });
    },
    onError: (failure) => {
      setCode('');
      setError(errorMessage(failure, messages.failed));
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => {
      if (!isPhotoType(file.type)) throw new Error(messages.tooBig);
      return uploadCampusPhoto({ contentType: file.type, sizeBytes: file.size }, file, setProgress);
    },
    onMutate: () => setProgress(0),
    onSuccess: (next) => {
      store(next);
      setPhoto(null);
      setError(null);
      draft.clear();
      setForm(EMPTY_FORM);
      showToast(messages.uploaded, { tone: 'positive' });
    },
    onError: (failure) =>
      setError(
        failure instanceof Error && failure.message === messages.tooBig
          ? messages.tooBig
          : errorMessage(failure, messages.failed),
      ),
    onSettled: () => setProgress(null),
  });

  const locate = () => {
    if (!('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        setNear({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  };

  const emailOk =
    form.method !== 'email' ||
    (/^\S+@\S+\.\S+$/.test(form.email.trim()) && emailMatches(form.email, campus));
  const ambassadorOk =
    form.ambassadorCode.trim() === '' || GrowthCodeSchema.safeParse(form.ambassadorCode).success;

  /** Which step of the flow to show. */
  const step: 'status' | 'code' | 'photo' | 'apply' = !pass
    ? 'apply'
    : pass.status === 'pending_email'
      ? 'code'
      : pass.status === 'pending_photo'
        ? 'photo'
        : 'status';

  return {
    loading: query.isLoading,
    failed: query.isError && !data,
    error: query.error ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    data,
    pass,
    step,
    passDays: data?.passDays ?? 0,
    /* campus lookup */
    campuses,
    locating,
    locate,
    /* form (draft-saved) */
    form,
    campus,
    pick: (campusId: string) => setForm((current) => ({ ...current, campusId })),
    setMethod: (method: CampusPassMethod) => setForm((current) => ({ ...current, method })),
    setEmail: (email: string) => {
      setError(null);
      setForm((current) => ({ ...current, email: email.slice(0, 120) }));
    },
    setAmbassadorCode: (value: string) =>
      setForm((current) => ({
        ...current,
        ambassadorCode: value
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
          .slice(0, 16),
      })),
    emailError:
      form.method === 'email' && form.email.trim() !== '' && !emailOk ? messages.emailBad : null,
    canStart: Boolean(form.campusId) && emailOk && ambassadorOk && !start.isPending,
    starting: start.isPending,
    start: () => start.mutate(),
    /** Start over from a rejected / expired pass or a stuck step. */
    reapply: () => {
      setCode('');
      setPhoto(null);
      setError(null);
      client.setQueryData<MyCampusPass>(key, (current) =>
        current ? { ...current, pass: null } : current,
      );
    },
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const kept = draft.keep();
      if (kept) setForm({ ...EMPTY_FORM, ...kept });
    },
    discardDraft: draft.discard,
    /* email code (memory only) */
    code,
    setCode: (value: string) => {
      setError(null);
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setCode(digits);
      if (CampusEmailVerifyInputSchema.safeParse({ code: digits }).success && !verify.isPending) {
        verify.mutate(digits);
      }
    },
    verifying: verify.isPending,
    verify: () => {
      if (!CampusEmailVerifyInputSchema.safeParse({ code }).success) return;
      verify.mutate(code);
    },
    /* ID photo */
    photoPreview: preview,
    progress,
    choosePhoto: (file: File) => {
      if (!isPhotoType(file.type) || file.size > CAMPUS_PHOTO_MAX_BYTES) {
        setError(messages.tooBig);
        return;
      }
      setError(null);
      setPhoto(file);
    },
    clearPhoto: () => setPhoto(null),
    uploading: upload.isPending,
    canUpload: photo !== null && !upload.isPending,
    upload: () => {
      if (photo) upload.mutate(photo);
    },
    formError: error,
  };
}
